import { useEffect, useState, useCallback } from "react";
import CompanyAdminLayout from "../../layouts/CompanyAdminLayout";
import { Download, Plus } from "lucide-react";
import { useToast } from "../../components/ToastContext";
import {
  getTeams,
  getCurrentUser,
  getOrganizationMembers,
} from "../../api/services/companyAdminService";
import AddUserModal from "../../components/CompanyAdmin/AddUserModal";
import UserTable from "../../components/CompanyAdmin/UserTable";
import "../../assets/styles/colors.css";

export default function UserManagement() {
  const { showToast } = useToast();
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Load teams
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoadingTeams(true);
      try {
        const data = await getTeams({ page: 1, pageSize: 100 });
        setTeams(Array.isArray(data?.teams) ? data.teams : []);
      } catch (error) {
        showToast(error.message || "Failed to load teams", "error");
      } finally {
        setIsLoadingTeams(false);
      }
    };
    loadTeams();
  }, [showToast]);

  // Load users
  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const me = await getCurrentUser();
      const orgId = me?.organization_id;
      if (!orgId) return;
      const data = await getOrganizationMembers(orgId, {
        page: 1,
        pageSize: 100,
      });
      setUsers(Array.isArray(data?.members) ? data.members : []);
    } catch (error) {
      showToast(error.message || "Failed to load users", "error");
    } finally {
      setIsLoadingUsers(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return (
    <CompanyAdminLayout>
      <div className="min-h-screen px-4 sm:px-8 py-6 sm:py-8 space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-(--color-text-primary)">
              User Management
            </h1>
            <p className="text-xs mt-0.5 text-(--color-text-muted)">
              Manage and monitor company users across all departments.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
            {/* <button
              disabled
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-(--color-text-muted) bg-(--color-bg-card) border border-(--color-border) rounded-lg text-sm opacity-60 cursor-not-allowed pointer-events-none"
            >
              <Download className="w-4 h-4" />
              Bulk Import (CSV)
            </button> */}
            <button
              onClick={() => setIsAddUserOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-white dark:text-black bg-(--color-primary) rounded-lg hover:opacity-90 transition-colors text-sm font-semibold"
            >
              <Plus className="w-4 h-4" />
              Add Single User
            </button>
          </div>
        </div>

        {/* Table */}
        <UserTable
          users={users}
          isLoading={isLoadingUsers}
          onUserUpdated={loadUsers}
        />
      </div>

      {/* Add user modal */}
      <AddUserModal
        isOpen={isAddUserOpen}
        teams={teams}
        isLoadingTeams={isLoadingTeams}
        onClose={() => setIsAddUserOpen(false)}
        onSuccess={loadUsers}
      />
    </CompanyAdminLayout>
  );
}
